import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DocumentMetadata, determineSize, fileSizeInMB } from '@core';
import { Unsubscriber } from '@core-lib/classes/unsubscriber.class';
import { IConfirmModalConfig, IParames } from '@core-lib/models';
import { ConfirmationService } from '@core-lib/services/confirmation-service.service';
import { ReferentialsService } from '@core-lib/services/referentials.service';
import { ModalRef } from '@core-lib/modules/modal-container';

@Component({
    selector: 'upload-document',
    templateUrl: './upload-document.component.html',
    styleUrls: ['./upload-document.component.scss'],
    standalone: false
})
export class UploadDocumentComponent extends Unsubscriber implements OnInit {
	@ViewChild('fileDropRef', { static: false }) fileDropEl: ElementRef;

	/**
	 * Tableau des fichiers
	 */
	_files: any[];

	@Input() set files(value: any[]) {
		if (value) {
			this._files = value;
		}
	}

	get files(): any[] {
		return this._files;
	}
	/**
	 * Propriété de communication pour la transmittion des fichiers au composant parent
	 */
	@Output() public fileDropped: EventEmitter<any> = new EventEmitter(); // Form

	/**
	 * Propriété de communication pour la transmittion d'un fichier au composant parent
	 */
	@Output() public sendFile: EventEmitter<any> = new EventEmitter(); // Document

	@Output() deleteAlfrescoCBDocId: EventEmitter<string> = new EventEmitter();

	/**
	 * Liste des extention des fichiers acceptés
	 */
	filesTypes: string = '*';

	/**
	 * Liste des types mime des fichiers acceptés
	 */
	mimeTypes: string = '*';

	/**
	 * Taille miximal autorisé pour les fichiers
	 */
	maxSize: number = 20000000; // (20Ko)

	@Input() alfrescoFiles: DocumentMetadata[];

	/**
	 * Si "false", l'utilisateur ne peut pas ajouter des fichiers
	 */
	@Input() canAddDocument: boolean = true;

	/**
	 * Si "false", l'utilisateur ne peut pas ajouter des fichiers
	 */
	@Input() viewDocumentChips: boolean = true;

	/**
	 * Liste des fichiers qui sont attachés aux coordonnées bancaires
	 */
	@Input() coordonneesBancairesFiles: File[];

	@Input() isFilesLoading: boolean = false;

	/**
	 * Constructeur
	 * @param modalRef La référence de l'objet modal
	 */
	constructor(
		public modalRef: ModalRef,
		private confirmationService: ConfirmationService,
		private referentialsService: ReferentialsService
	) {
		super();
	}

	ngOnInit(): void {
		// Sélectionner la liste de types mime des fichiers depuis la table Parametres dans la base de données si un paramétrage est predinifit
		this.referentialsService.getReferentialParameters().subscribe((params: IParames) => {
			if (params) {
				// Définir les extentions des fichiers autorisées
				if (params?.listeParametres?.some(p => p.code == 'FILEEXT')) {
					this.filesTypes = params?.listeParametres?.find(p => p.code === 'FILEEXT').valeur;
				}

				// Définir les types mime des fichiers autorisés
				if (params?.listeParametres?.some(p => p.code == 'MIMETYPE')) {
					this.mimeTypes = params?.listeParametres?.find(p => p.code === 'MIMETYPE').valeur;
				}

				// Définir la taille maximale autorisée pour les fichiers
				if (params?.listeParametres?.some(p => p.code == 'TAILLEMAXUPLOAD')) {
					this.maxSize = +params?.listeParametres?.find(p => p.code === 'TAILLEMAXUPLOAD').valeur;
				}
			}
		});
	}

	ngAfterViewInit(): void {}

	/**
	 * Evénement sur glisser/déposer fichier(s)
	 * @param $event
	 */
	onFileDropped($event: any): void {
		this.addFile($event);
	}

	/**
	 * Evénement sur parcourir le(s) fichier(s)
	 * @param files
	 */
	fileBrowseHandler(files: any): void {
		this.addFile(files);
	}

	/**
	 * Ajouter fichier(s) dans le tableau @files
	 * @param files
	 */
	private addFile(files: Array<File>): void {
		const validFiles = [];
		let totalFileSizeBytes = 0;

		for (const item of files) {
			const fileAlreadySelected =
				(this.files && this.files.some(f => f.name === item.name)) ||
				(this.alfrescoFiles && this.alfrescoFiles.some(f => f.name === item.name)) ||
				(this.coordonneesBancairesFiles && this.coordonneesBancairesFiles.some(f => f.name === item.name));

			// Vérifier si le fichier est déjà sélectionné: un fichier avec le même nom est déjà sélectionné
			if (fileAlreadySelected) {
				this.showModal('Documents.Form.DuplicatedFile', { nom: item.name });
			}
			// Vérifier le type de fichier et la taille
			else if (item.size > this.maxSize) {
				this.showModal('Documents.Form.TooLargeFile', { nom: item.name, maxSize: determineSize(this.maxSize) });
			} else if (item.size === 0) {
				this.showModal('Documents.Form.TooSmallFile');
			} else if (this.mimeTypes !== '*' && !this.mimeTypes.includes(item.type)) {
				this.showModal('Documents.Form.NotSuportedFileType', { nom: item.name });
			} else {
				validFiles.push(item);
				totalFileSizeBytes += item.size;
			}
		}

		// Vérifier la taille totale des fichiers
		const totalFileSizeMB = fileSizeInMB(totalFileSizeBytes);
		if (totalFileSizeMB > 25) {
			// Afficher un message d'erreur ou empêcher l'ajout des fichiers
			this.showModal('Documents.Form.TooLargeFiles');
			return;
		}

		// Ajouter les fichiers valides à la liste des fichiers
		this.files = !this.files ? [] : this.files.concat(validFiles);

		// Émettre les événements appropriés
		this.fileDropped.emit(this.files);
		this.sendFile.emit(validFiles);

		// Effacer la valeur de l'élément de dépôt de fichier
		this.fileDropEl.nativeElement.value = '';
	}

	/**
	 * Suppression de fihcier(s) du tableau @files
	 * @param index
	 */
	deleteFile(index: number): void {
		this.files.splice(index, 1);
		this.fileDropped.emit(this.files);
	}

	deleteAlfrescoFile(id: string): void {
		this.deleteAlfrescoCBDocId.emit(id);
	}

	/**
	 * Afficher une modal de notification
	 * @param  textsKey
	 * @param bodyParams
	 */
	showModal(textsKey: string, bodyParams?: any): void {
		const config: IConfirmModalConfig = {
			confirmBtnText: 'Actions.Ok',
			canClose: false,
			canConfirm: true,
			textsKey,
			bodyParams
		};
		this.confirmationService.openConfirmationModal(config);
	}
}
